import {
    Button as KitButton,
    LinkProvider,
    PLATFORM_ICONS,
    type ButtonProps as KitButtonProps,
    type IconSource,
    type PlatformIconName,
} from '@remotely/ui-kit';
import {
    useRef,
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from 'react';
import Icon, { RemixIconNames, RemixIconsInUse, type IconNames } from '~/components/ui/Icon';
import { ReloadDocumentLink } from '~/components/ui/RouterLink';
import Tooltip from '~/components/ui/Tooltip';
import '~/styles/components/new/button.scss';
import { classNames, type IconColor } from '~/utils/styles';
import Divider from './Divider';

// common props for Button and ButtonLink
type ButtonCommonProps = {
    action?: 'primary' | 'negative' | 'positive';
    disabled?: boolean; // included here bcs is not part of the html anchor props
    processing?: boolean;
    variant?: 'filled' | 'filled-light' | 'floating';
    icon?: IconNames;
    size?: 'medium' | 'large';
    'data-test-component'?: string;
    'data-test-name'?: string;
};

/**
 * BaseButton
 * This is a common component for Button and ButtonIcon, do not use it directly or export it.
 * It accepts a icon component instead of icon name for convenience to avoid passing variant and action just to get the icon color.
 */

type ButtonWithText = {
    children: ReactNode;
    iconComponent?: ReactElement<typeof Icon>;
    title?: string;
};

type IconOnlyButton = {
    children?: undefined | null; // includes null as part of the HTML button type;
    // required for icon-only
    iconComponent: ReactElement<typeof Icon>;
    title: string;
};

type ButtonIconRule = ButtonWithText | IconOnlyButton;

type BaseButtonProps = ButtonIconRule & ComponentPropsWithRef<'button'>;

const BaseButton = ({
    iconComponent,
    children,
    // html button props
    title,
    className = '',
    ...props
}: BaseButtonProps) => {
    return (
        <Tooltip title={title}>
            <button
                {...props}
                className={classNames({
                    button: true,
                    'button-rounded': children === undefined,
                    [className]: Boolean(className),
                })}>
                {iconComponent}
                {children}
            </button>
        </Tooltip>
    );
};

type iconColorActionVars = Extract<IconColor, `action-${string}`>;

const ICON_COLOR_BY_ACTION: Record<string, iconColorActionVars> = {
    primary: 'action-primary',
    negative: 'action-negative-dark',
    positive: 'action-positive-dark',
};

// helper that applies the rules for icon and icon color.
function getIconComponent(
    processing: boolean,
    variant: ButtonCommonProps['variant'],
    action: ButtonCommonProps['action'] = 'primary',
    icon?: IconNames,
) {
    const iconName = processing ? 'loading' : icon;

    // note that loading indicator shows even when no icon is provided
    return iconName ? (
        <Icon
            icon={iconName}
            size={16}
            colorVar={
                variant !== 'filled'
                    ? ICON_COLOR_BY_ACTION[action] // since floating and filled-light make clear background, paints icon as action color
                    : 'action-contrast' // otherwise, make it white
            }
        />
    ) : undefined;
}

/**
 * Button
 */

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type KitButtonButtonProps = Extract<KitButtonProps, { href?: undefined }>;
type KitButtonLinkProps = Extract<KitButtonProps, { href: string }>;

export type ButtonIconName = keyof typeof RemixIconsInUse | PlatformIconName;

// title stays optional on icon-only buttons, as it was before the ui-kit Button
type PlatformButtonProps = {
    icon?: ButtonIconName;
    title?: string;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

export type ButtonProps = DistributiveOmit<KitButtonButtonProps, 'icon' | 'title'> &
    PlatformButtonProps;

export function toIconSource(icon: ButtonIconName): IconSource;
export function toIconSource(icon?: ButtonIconName): IconSource | undefined;
export function toIconSource(icon?: ButtonIconName): IconSource | undefined {
    if (!icon) return undefined;
    return isKitIcon(icon) ? icon : RemixIconsInUse[icon];
}

function isKitIcon(icon: ButtonIconName): icon is PlatformIconName {
    return PLATFORM_ICONS.some((name) => name === icon);
}

export default function Button({
    icon,
    'data-test-component': testComponent = 'new/Button',
    ...props
}: ButtonProps) {
    return (
        <KitButton
            {...(props as KitButtonButtonProps)}
            icon={toIconSource(icon)}
            data-test-component={testComponent}
        />
    );
}

type LegacyButtonProps = ButtonCommonProps & Omit<BaseButtonProps, 'iconComponent'>;

function LegacyButton({
    variant = 'filled',
    action = 'primary',
    processing = false,
    size = 'large',
    icon,
    children,
    // html button props
    disabled = false,
    className = '',
    type = 'button', // default to button, for not submiting forms
    'data-test-component': testComponent = 'new/Button',
    'data-test-name': testName,
    // more html props
    ...props
}: LegacyButtonProps) {
    return (
        <BaseButton
            {...props}
            iconComponent={getIconComponent(processing, variant, action, icon)}
            disabled={disabled || processing}
            className={classNames({
                [`size-${size} action-${action} variant-${variant}`]: true,
                [className]: Boolean(className),
            })}
            type={type}
            data-test-component={testComponent}
            data-test-name={testName}>
            {children}
        </BaseButton>
    );
}

/**
 * ButtonSplit
 */

export type ButtonSplitProps = ButtonCommonProps &
    Omit<BaseButtonProps, 'iconComponent' | 'variant'> & {
        onClickSplit?: (ev: MouseEvent<HTMLButtonElement>) => void;
        splitContent?: ReactNode;
    };

export function ButtonSplit({
    onClickSplit,
    // Button props
    action = 'primary',
    icon,
    processing = false,
    size = 'large',
    splitContent,
    children,
    // html button props
    disabled,
    className = '',
    onClick,
    // more html button props
    ...props
}: ButtonSplitProps) {
    const appletRef = useRef<HTMLElement>(null!);

    // only filled variant is supported for split button
    const variant = 'filled';

    return (
        <BaseButton
            {...props}
            disabled={disabled || processing}
            iconComponent={getIconComponent(processing, variant, action, icon)}
            onClick={(ev) => {
                const x = appletRef.current.getBoundingClientRect().x;
                if (ev.clientX > x) {
                    onClickSplit?.(ev);
                } else {
                    onClick?.(ev);
                }
            }}
            className={classNames({
                [`button-split size-${size} action-${action} variant-${variant}`]: true,
                [className]: Boolean(className),
            })}>
            <div className='split-content'>{children}</div>
            <SplitComponent ref={appletRef}>
                {splitContent ?? (
                    <Icon
                        icon={RemixIconNames.RiArrowDropDownLine}
                        size={16}
                    />
                )}
            </SplitComponent>
        </BaseButton>
    );
}

function SplitComponent({ ref, children }: { ref?: RefObject<HTMLElement>; children?: ReactNode }) {
    return (
        <>
            <Divider
                vertical
                className='button-divider'
                ref={ref}
            />
            {children}
        </>
    );
}

/**
 * ButtonLink
 */

export type ButtonLinkProps = DistributiveOmit<KitButtonLinkProps, 'icon' | 'title' | 'href'> &
    PlatformButtonProps & {
        to: string;
        reloadDocument?: boolean;
    };

export function ButtonLink({ to, icon, reloadDocument, ...props }: ButtonLinkProps) {
    const button = (
        <KitButton
            {...(props as DistributiveOmit<KitButtonLinkProps, 'href'>)}
            href={to}
            icon={toIconSource(icon)}
        />
    );

    if (!reloadDocument) {
        return button;
    }

    return <LinkProvider component={ReloadDocumentLink}>{button}</LinkProvider>;
}

/**
 * PaginationButton
 */
export type PaginationButtonProps = Pick<
    LegacyButtonProps,
    'onClick' | 'disabled' | 'className' | 'children'
> & {
    role?: 'number' | 'next' | 'prev' | 'more';
    selected?: boolean;
};

export function PaginationButton({
    onClick,
    disabled,
    className = '',
    children,
    role = 'number',
    selected = false,
}: PaginationButtonProps) {
    const icon = ICON_BY_ROLE[role];
    return (
        <LegacyButton
            icon={icon}
            disabled={disabled}
            variant={selected ? 'filled' : 'filled-light'}
            className={classNames({
                'button button-rounded button-pagination': true,
                selected,
                more: role === 'more',
                [className]: Boolean(className),
            })}
            onClick={selected || role === 'more' ? undefined : onClick}
            size='medium'
            data-test-component='new/PaginationButton'
            data-test-name={role}>
            {role === 'number' ? children : undefined}
        </LegacyButton>
    );
}
export const ICON_BY_ROLE: Record<string, IconNames> = {
    // no icon for role number
    next: RemixIconNames.RiArrowRightSLine,
    prev: RemixIconNames.RiArrowLeftSLine,
    more: RemixIconNames.RiMoreFill,
};

/**
 * ButtonGroup
 */

export type ButtonGroupAlignment = 'left' | 'right' | 'center' | 'sides';

const ButtonGroupAligns = {
    left: 'justify-start',
    right: 'justify-end',
    center: 'justify-center',
    sides: 'justify-between',
    modal: 'modal-footer',
};

export type ButtonGroupProps = {
    align?: ButtonGroupAlignment;
    noSpace?: boolean;
    children: ReactNode;
    className?: string;
};

export function ButtonGroup({ align = 'right', children, className = '' }: ButtonGroupProps) {
    const alignClass = ButtonGroupAligns[align] || 'justify-between';

    return (
        <div
            className={classNames({
                [`button-group ${alignClass}`]: true,
                [className]: Boolean(className),
            })}>
            {children}
        </div>
    );
}
